import { Directive, ElementRef, forwardRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[appCurrencyFormatter]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyFormatterDirective),
      multi: true,
    },
  ],
})
export class CurrencyFormatterDirective implements ControlValueAccessor {
  private el: HTMLInputElement;
  private decimalSeparator: string = '.';

  constructor(private elementRef: ElementRef) {
    this.el = this.elementRef.nativeElement;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    let value = this.el.value;
    const selectionStart = this.el.selectionStart;

    // Permitir números, un único punto y el signo negativo al inicio
    let cleaned = value.replace(/[^0-9.-]/g, '')
      .replace(/(\..*)\./g, '$1') // Eliminar múltiples puntos
      .replace(/^-+/, '-')       // Permitir un solo signo negativo al inicio
      .replace(/^-\./, '-0.');   // Asegurar que "-." se convierta en "-0."

    this.el.value = cleaned; // Actualizamos el valor inmediatamente con la entrada "limpia"

    const parts = cleaned.split('.');
    let formatted = '';

    if (parts[0]) {
      formatted = parseInt(parts[0], 10).toLocaleString('en-US');
    }

    if (parts.length > 1) {
      formatted += '.' + parts[1];
    }

    if (this.el.value !== formatted) {
      this.el.value = formatted;
      this.restoreCaretPosition(this.el, selectionStart, value, formatted);
    }

    // Actualizar el valor del formulario (sin formatear)
    this.onChange(cleaned === '' || cleaned === '-' || cleaned === '-0' ? null : parseFloat(cleaned.replace(/,/g, '')));
    event.preventDefault();
  }

  private restoreCaretPosition(input: HTMLInputElement, oldPosition: number | null, oldValue: string, newValue: string) {
    if (oldPosition === null) return;
    setTimeout(() => {
      let newPosition = 0;
      let integersBefore = oldValue.substring(0, oldPosition).replace(/[^0-9.]/g, '').length;
      let newIntegerPart = newValue.split('.')[0].replace(/[^0-9]/g, '');

      newPosition = newValue.split('.')[0].length;
      if (newValue.includes('.')) {
        newPosition = Math.min(newValue.length, newValue.indexOf('.') + (oldValue.substring(oldValue.indexOf('.')).length));
      } else if (integersBefore <= newIntegerPart.length) {
        newPosition = newValue.length;
      }

      input.setSelectionRange(newPosition, newPosition);
    });
  }

  // ControlValueAccessor methods
  onChange: (value: number | null) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: number | null): void {
    if (value !== null) {
      this.el.value = value.toLocaleString('en-US', { minimumFractionDigits: value % 1 === 0 ? 0 : undefined, maximumFractionDigits: 2 });
    } else {
      this.el.value = '';
    }
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.disabled = isDisabled;
  }
}
